import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import VisitorTracker from './components/VisitorTracker';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import TeamsPage from './pages/TeamsPage';
import PlayersPage from './pages/PlayersPage';
import MatchesPage from './pages/MatchesPage';
import StandingsPage from './pages/StandingsPage';
import PlayerStatsPage from './pages/PlayerStatsPage';
import SponsorsPage from './pages/SponsorsPage';
import RefereesPage from './pages/RefereesPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import PublicTournamentPage from './pages/PublicTournamentPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RefereeProfilePage from './pages/RefereeProfilePage';
import TournamentInfoPage from './pages/TournamentInfoPage';
import RefereeAssignmentsPage from './pages/RefereeAssignmentsPage';
import BlogPage from './pages/BlogPage';
import AnalyticsPage from './pages/AnalyticsPage';
import GalleryPage from './pages/GalleryPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-emerald-500 text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check for session_id synchronously during render (before useEffect)
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/t/:subdomain" element={<PublicTournamentPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute><OnboardingPage /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      <Route path="/teams" element={
        <ProtectedRoute><TeamsPage /></ProtectedRoute>
      } />
      <Route path="/players" element={
        <ProtectedRoute><PlayersPage /></ProtectedRoute>
      } />
      <Route path="/matches" element={
        <ProtectedRoute><MatchesPage /></ProtectedRoute>
      } />
      <Route path="/standings" element={
        <ProtectedRoute><StandingsPage /></ProtectedRoute>
      } />
      <Route path="/stats" element={
        <ProtectedRoute><PlayerStatsPage /></ProtectedRoute>
      } />
      <Route path="/sponsors" element={
        <ProtectedRoute><SponsorsPage /></ProtectedRoute>
      } />
      <Route path="/referees" element={
        <ProtectedRoute><RefereesPage /></ProtectedRoute>
      } />
      <Route path="/referee-assignments" element={
        <ProtectedRoute><RefereeAssignmentsPage /></ProtectedRoute>
      } />
      <Route path="/tournament-info" element={
        <ProtectedRoute><TournamentInfoPage /></ProtectedRoute>
      } />
      <Route path="/blog" element={
        <ProtectedRoute><BlogPage /></ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute><AnalyticsPage /></ProtectedRoute>
      } />
      <Route path="/gallery" element={
        <ProtectedRoute><GalleryPage /></ProtectedRoute>
      } />
      <Route path="/referee/:refereeId" element={<RefereeProfilePage />} />
      <Route path="/settings" element={
        <ProtectedRoute><SettingsPage /></ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute><BillingPage /></ProtectedRoute>
      } />
      <Route path="/billing/success" element={
        <ProtectedRoute><BillingSuccessPage /></ProtectedRoute>
      } />
      {/* Catch-all: direct subdomain URLs like /cnvt */}
      <Route path="/:subdomain" element={<PublicTournamentPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VisitorTracker />
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
